import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme } from '../../theme';

const ORANGE = '#F18A00';
const BLACK  = '#161616';

const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);

const lbl = { fontSize:12, color:'#64748B', display:'block', marginBottom:6, fontWeight:500 };
const inp = { border:'1px solid #D9D9D5', borderRadius:8, padding:'10px 12px', width:'100%', fontSize:14, boxSizing:'border-box', fontFamily:'inherit', outline:'none' };

function PasswordField({ label, value, onChange, placeholder }) {
  const [focus, setFocus] = useState(false);
  const [show, setShow] = useState(false);
  return (
    <div style={{ marginBottom:14 }}>
      <label style={lbl}>{label}</label>
      <div style={{ position:'relative' }}>
        <input type={show ? 'text' : 'password'} value={value || ''} onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
          style={{ ...inp, paddingRight:42,
            borderColor: focus ? ORANGE : '#D9D9D5',
            boxShadow: focus ? '0 0 0 3px rgba(241,138,0,0.15)' : 'none',
            transition: 'all .12s' }}/>
        <button type="button" onClick={()=>setShow(s=>!s)}
          style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
            background:'none', border:'none', cursor:'pointer', padding:4, display:'flex' }}>
          {show
            ? <SvgIcon size={17} color="#94A3B8"><path d="M10.585 10.587a2 2 0 0 0 2.829 2.828M16.681 16.673a8.7 8.7 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674M9 5.61a9.4 9.4 0 0 1 3 -.61c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87M3 3l18 18"/></SvgIcon>
            : <SvgIcon size={17} color="#94A3B8"><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"/></SvgIcon>}
        </button>
      </div>
    </div>
  );
}

export default function MonCompte() {
  const navigate = useNavigate();
  const { user, changePassword } = useAuth();
  const { t } = useLanguage();

  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState(false);

  async function submit() {
    setError(''); setSuccess(false);
    if (!oldPwd || !newPwd || !confirmPwd) { setError(t('auth.fill_all')); return; }
    if (newPwd.length < 8) { setError(t('auth.password_too_short')); return; }
    if (newPwd !== confirmPwd) { setError(t('auth.password_mismatch')); return; }
    setSaving(true);
    try {
      await changePassword(oldPwd, newPwd);
      setSuccess(true);
      setOldPwd(''); setNewPwd(''); setConfirmPwd('');
      setTimeout(() => setSuccess(false), 4000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  if (!user) return null;
  const fullName = [user.prenom, user.nom].filter(Boolean).join(' ') || user.email;
  const roleLabel = t('nav.role_' + user.role) || user.role;

  return (
    <div style={{ maxWidth:680, margin:'0 auto' }}>
      <div style={{ background:'#fff', borderRadius:12, border:'0.5px solid #E8E8E5', overflow:'hidden', marginBottom:16 }}>
        {/* Header clair (style "Ligne") */}
        <div style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:13, borderBottom:`2px solid ${theme.borderDark}` }}>
          <div style={{ width:42, height:42, borderRadius:'50%', background:theme.sidebarBg,
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <SvgIcon size={22} color={ORANGE}><path d="M9 7a4 4 0 1 0 0 0M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/></SvgIcon>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:11, color:theme.textTertiary, textTransform:'uppercase', letterSpacing:'.04em' }}>{t('account.breadcrumb')}</div>
            <div style={{ fontSize:22, fontWeight:800, color:theme.text }}>{t('account.title')}</div>
          </div>
        </div>

        {/* Profil — lecture seule */}
        <div style={{ padding:'18px 20px' }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#1A1A1A', marginBottom:14, display:'flex', alignItems:'center', gap:8 }}>
            <SvgIcon size={17} color={ORANGE}><path d="M9 7a4 4 0 1 0 0 0M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/></SvgIcon>
            {t('account.profile')}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <div style={lbl}>{t('account.field_nom')}</div>
              <div style={{ ...inp, background:'#F7F7F5', color:'#1A1A1A', fontWeight:500 }}>{fullName}</div>
            </div>
            <div>
              <div style={lbl}>{t('account.field_email')}</div>
              <div style={{ ...inp, background:'#F7F7F5', color:'#1A1A1A' }}>{user.email}</div>
            </div>
            <div>
              <div style={lbl}>{t('account.field_role')}</div>
              <div style={{ ...inp, background:'#F7F7F5', display:'flex', alignItems:'center' }}>
                <span style={{ background:'#FEF3E2', color:'#9A5A00', padding:'3px 10px', borderRadius:99, fontSize:12, fontWeight:500 }}>
                  {roleLabel}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Changer mot de passe */}
      <div style={{ background:'#fff', borderRadius:12, border:'0.5px solid #E8E8E5', overflow:'hidden' }}>
        <div style={{ padding:'14px 20px', borderBottom:'0.5px solid #F1F1EE' }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#1A1A1A', display:'flex', alignItems:'center', gap:8 }}>
            <SvgIcon size={17} color={ORANGE}><path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .007h-1.977a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.977a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0zM15 9h.01"/></SvgIcon>
            {t('account.change_password')}
          </div>
        </div>

        <div style={{ padding:'18px 20px' }}>
          {error && <div style={{ background:'#FCEBEB', border:'1px solid #F09595', borderRadius:8, padding:'9px 14px', marginBottom:14, fontSize:13, color:'#A32D2D' }}>⚠ {error}</div>}
          {success && <div style={{ background:'#E1F5EE', border:'1px solid #B6E5D2', borderRadius:8, padding:'9px 14px', marginBottom:14, fontSize:13, color:'#0F6E56' }}>✓ {t('account.password_changed')}</div>}

          <PasswordField label={t('account.current_password')} value={oldPwd} onChange={setOldPwd} placeholder="••••••••"/>
          <PasswordField label={t('auth.new_password')} value={newPwd} onChange={setNewPwd} placeholder="••••••••"/>
          <PasswordField label={t('auth.confirm_password')} value={confirmPwd} onChange={setConfirmPwd} placeholder="••••••••"/>

          <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:6 }}>
            <button onClick={() => navigate(-1)}
              style={{ background:'#fff', color:'#64748B', border:'1px solid #D9D9D5', borderRadius:8, padding:'9px 16px', fontSize:14, cursor:'pointer' }}>
              {t('common.cancel')}
            </button>
            <button onClick={submit} disabled={saving}
              style={{ background:ORANGE, color:BLACK, border:'none', borderRadius:8, padding:'9px 18px', fontSize:14, fontWeight:500,
                cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? .5 : 1 }}>
              {saving ? '…' : t('account.update_password_btn')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
